/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Leonid Khramov
 */
import React, { Component } from "react"
import Hls from "hls.js"

import "./EditSpotlight.css"

const apiEndpoint = process.env.REACT_APP_API

export default class EditSpotlight extends Component {
    constructor(props) {
        super(props)

        this.state = {
            utc: 0,
            time: 1,
            x: 500,
            y: 500,
            scale: 1,
            loading: false,
        }

        if (this.props.spotlight) {
            this.state = Object.assign(this.state, this.props.spotlight)
        }

        this.onTouchStart = this.onTouchStart.bind(this)
        this.onTouchMove = this.onTouchMove.bind(this)
        this.onTouchEnd = this.onTouchEnd.bind(this)
        this.onForward = this.onForward.bind(this)
        this.onBackward = this.onBackward.bind(this)
        this.videoReady = this.videoReady.bind(this)
        this.scrub = this.scrub.bind(this)

        if (props.onSpotlightChange) {
            props.onSpotlightChange({
                time: this.state.time,
                x: this.state.x,
                y: this.state.y,
                scale: this.state.scale,
            })
        }
    }

    onForward(e) {
        e.preventDefault()
        let time = this.state.time * 1 + 0.5
        let max = this.props.clip ? this.props.clip.dynamic.duration : 20
        if (time > max) time = max
        this.setTime(time)
    }

    onBackward(e) {
        e.preventDefault()
        let time = this.state.time - 0.5
        if (time < 0) time = 0
        this.setTime(time)
    }

    scrub(e) {
        let time = e.target.value
        this.setTime(time)
    }

    setTime(time) {
        this.setState({ time: time * 1 })

        let video = document.getElementById("spotlight-editor-video")
        video.currentTime = time
        if (this.props.onSpotlightChange) {
            this.props.onSpotlightChange({
                time: time,
                x: this.state.x,
                y: this.state.y,
                scale: this.state.scale,
            })
        }
    }

    onTouchStart(e) {
        e.preventDefault()
        this.moving = true
        if (e.touches && e.touches.length > 0) {
            this.startX = e.touches[0].pageX
            this.startY = e.touches[0].pageY
        } else {
            this.startX = e.pageX
            this.startY = e.pageY
        }
    }

    onTouchMove(e) {
        e.preventDefault()

        var video = document.getElementById("spotlight-editor-video")
        let vw = video.offsetWidth / 1000
        let vh = video.offsetHeight / 1000

        if (this.moving === true) {
            if (e.touches && e.touches.length > 0) {
                let dx = (e.touches[0].pageX - this.startX) / vw
                let dy = (e.touches[0].pageY - this.startY) / vh
                this.startX = e.touches[0].pageX
                this.startY = e.touches[0].pageY

                let x = this.state.x + dx
                let y = this.state.y + dy
                let scale = y / 500

                // cap values
                if (scale < 0.4) {
                    scale = 0.4
                }
                if (scale > 2) {
                    scale = 2
                }
                if (x < 50) {
                    x = 50
                }
                if (x > 950) {
                    x = 950
                }
                if (y < 50) {
                    y = 50
                }
                if (y > 950) {
                    y = 950
                }

                this.setState({ x, y, scale })
            } else {
                let dx = (e.pageX - this.startX) / vw
                let dy = (e.pageY - this.startY) / vh
                this.startX = e.pageX
                this.startY = e.pageY

                let x = this.state.x + dx
                let y = this.state.y + dy
                let scale = y / 500

                // cap values
                if (scale < 0.4) {
                    scale = 0.4
                }
                if (scale > 2) {
                    scale = 2
                }
                if (x < 50) {
                    x = 50
                }
                if (x > 950) {
                    x = 950
                }
                if (y < 50) {
                    y = 50
                }
                if (y > 950) {
                    y = 950
                }

                this.setState({ x, y, scale })
            }

            if (this.props.onSpotlightChange) {
                this.props.onSpotlightChange({
                    time: this.state.time,
                    x: this.state.x,
                    y: this.state.y,
                    scale: this.state.scale,
                })
            }
        }
    }

    onTouchEnd(e) {
        e.preventDefault()
        this.moving = false
    }

    componentDidMount() {
        if (this.props.clip) {
            this.loadVideo()
        }

        var overlay = document.querySelector(".SpotlightOverlay")
        overlay.addEventListener("mousedown", this.onTouchStart)
        overlay.addEventListener("mousemove", this.onTouchMove)
        overlay.addEventListener("mouseup", this.onTouchEnd)
        overlay.addEventListener("mouseleave", this.onTouchEnd)

        overlay.addEventListener("touchstart", this.onTouchStart)
        overlay.addEventListener("touchmove", this.onTouchMove)
        overlay.addEventListener("touchend", this.onTouchEnd)
        overlay.addEventListener("touchcancel", this.onTouchEnd)
    }

    componentWillUnmount() {
        var overlay = document.querySelector(".SpotlightOverlay")
        overlay.removeEventListener("mousedown", this.onTouchStart)
        overlay.removeEventListener("mousemove", this.onTouchMove)
        overlay.removeEventListener("mouseup", this.onTouchEnd)
        overlay.removeEventListener("mouseleave", this.onTouchEnd)

        overlay.removeEventListener("touchstart", this.onTouchStart)
        overlay.removeEventListener("touchmove", this.onTouchMove)
        overlay.removeEventListener("touchend", this.onTouchEnd)
        overlay.removeEventListener("touchcancel", this.onTouchEnd)
    }

    componentDidUpdate() {
        if (this.props.clip) {
            this.loadVideo()
        }
    }

    loadVideo() {
        var video = document.getElementById("spotlight-editor-video")
        video.volume = 0
        let clip = this.props.clip
        let teamName = this.props.teamName
        let gameId = this.props.gameId

        const hlsUrl =
            apiEndpoint +
            "/teams/" +
            teamName +
            "/games/" +
            teamName +
            "-" +
            gameId +
            "/" +
            clip.dynamic.hls
        let nativeHls =
            video.canPlayType &&
            !!video.canPlayType("application/vnd.apple.mpegURL")

        // Make sure we just load once
        if (this.loadingVideoUrl === hlsUrl) {
            return
        }
        this.loadingVideoUrl = hlsUrl

        if (nativeHls) {
            video.setAttribute("src", hlsUrl)
            video.load()
            video.addEventListener("canplay", this.videoReady)
        } else if (Hls.isSupported()) {
            //this.cleanHls()
            this.hls = new Hls()
            this.hls.loadSource(hlsUrl)
            this.hls.attachMedia(video)
            this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
                this.videoReady()
            })
        }
    }

    videoReady() {
        var video = document.getElementById("spotlight-editor-video")
        video.removeEventListener("canplay", this.videoReady)

        video.currentTime = this.state.time
        video.pause()
        this.setState({ loaded: true })
    }

    render() {
        let video = document.getElementById("spotlight-editor-video")
        let duration = this.props.clip ? this.props.clip.dynamic.duration : 20
        let overlayStyles = {}
        if (video) {
            let vw = video.offsetWidth
            let vh = video.offsetHeight

            let xpos = (this.state.x / 1000) * vw
            let ypos = (this.state.y / 1000) * vh

            let bw = this.state.scale * (video.offsetWidth * 5)
            let bh = this.state.scale * (video.offsetWidth * 5)
            console.log("EditSpotlight.render bw", bw)

            let bx = bw / -2 + xpos
            let by = bh / -2 + ypos

            overlayStyles["backgroundSize"] = `${bw}px ${bh}px`
            overlayStyles["backgroundPosition"] = `${bx}px ${by}px`

            console.log(
                "EditSpotlight x",
                this.state.x,
                "y",
                this.state.y,
                "vw",
                vw,
                "vh",
                vh
            )
        }

        let fw = (this.state.time / this.props.clip.dynamic.duration) * 100
        let vt = ((50 - fw) * 10) / 100
        let sliderFillStyles = {
            width: `calc(${fw}% + ${vt}px)`,
        }

        console.log(
            "Slider style",
            fw,
            this.state.time,
            this.props.clip.dynamic.duration
        )
        let coverStyle = this.props.disabled ? "block" : "none"
        overlayStyles.display = this.props.disabled ? "none" : "block"

        return (
            <div className="EditSpotlightContainer">
                <div className="EditSpotlight">
                    <video id="spotlight-editor-video" />
                    <div style={overlayStyles} className="SpotlightOverlay" />
                </div>
                {!this.props.disabled && (
                    <div className="slider">
                        <div className="range-fill" style={sliderFillStyles} />
                        <input
                            type="range"
                            className="spotlight-range"
                            min="0"
                            max={duration}
                            step="0.5"
                            onChange={this.scrub}
                            value={this.state.time}
                        />
                    </div>
                )}
                {this.props.disabled && (
                    <div className="slider" style={{ height: "10px" }} />
                )}
                <div className="EditSpotlight-Time">
                    <a href="jump-back" onClick={this.onBackward}>
                        -0.5 sec
                    </a>
                    <div className="divider"></div>
                    <a href="jump-forward" onClick={this.onForward}>
                        +0.5 sec
                    </a>
                    <div className="cover" style={{ display: coverStyle }} />
                </div>
            </div>
        )
    }
}
